import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useFeedbackCustomers,
  useProcessCustomerFeedback,
  useSaveCustomerFeedback,
  type FeedbackWithContext,
} from '@/features/customerFeedback/hooks';
import {
  FEEDBACK_CHANNELS,
  FEEDBACK_CHANNEL_META,
  describeFeedbackError,
} from '@/features/customerFeedback/model';
import { todayIsoDate } from '@/lib/formatters';
import type { CustomerFeedbackChannel } from '@/types/database';

interface FeedbackEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit an existing entry instead of logging a new one. */
  existing?: FeedbackWithContext | null;
  /** Called with the saved entry id. */
  onSaved?: (id: string) => void;
}

function isoDateOf(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Turn a `YYYY-MM-DD` from the picker into a timestamp. Today keeps the
 * current time so the feed sorts a just-logged call above this
 * morning's email; any other day lands at noon local so timezone shifts
 * never move it to the neighbouring day.
 */
function occurredAtFrom(isoDate: string, previous: string | null): string | null {
  if (!isoDate) return null;
  if (previous && isoDateOf(previous) === isoDate) return previous;
  if (isoDate === todayIsoDate()) return new Date().toISOString();
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

/**
 * "Log interaction" — paste what the customer said, add your own notes,
 * and (optionally) have the tasks extracted straight away.
 */
export function FeedbackEntryDialog({
  open,
  onOpenChange,
  existing = null,
  onSaved,
}: FeedbackEntryDialogProps) {
  const customersQ = useFeedbackCustomers();
  const save = useSaveCustomerFeedback();
  const process = useProcessCustomerFeedback();

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [channel, setChannel] = useState<CustomerFeedbackChannel>('email');
  const [occurredOn, setOccurredOn] = useState(todayIsoDate());
  const [interaction, setInteraction] = useState('');
  const [repNotes, setRepNotes] = useState('');
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const customerFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setCustomerId(existing?.customer_id ?? null);
    setCustomerName(existing?.customer?.name ?? existing?.customer_name ?? '');
    setChannel(existing?.channel ?? 'email');
    setOccurredOn(existing ? isoDateOf(existing.occurred_at) : todayIsoDate());
    setInteraction(existing?.interaction ?? '');
    setRepNotes(existing?.rep_notes ?? '');
    setCustomerMenuOpen(false);
  }, [open, existing]);

  // Close the customer suggestions when clicking elsewhere in the dialog.
  useEffect(() => {
    if (!customerMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!customerFieldRef.current?.contains(e.target as Node)) setCustomerMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [customerMenuOpen]);

  const customers = useMemo(() => customersQ.data ?? [], [customersQ.data]);
  const matches = useMemo(() => {
    const q = customerName.trim().toLowerCase();
    const pool = q
      ? customers.filter(
          (c) => c.name.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q),
        )
      : customers;
    return pool.slice(0, 8);
  }, [customers, customerName]);

  const linkedCustomer = customerId ? (customers.find((c) => c.id === customerId) ?? null) : null;
  const busy = save.isPending;
  const canSubmit = interaction.trim().length > 0 && !busy;

  const submit = async (extract: boolean) => {
    if (!canSubmit) return;
    try {
      const saved = await save.mutateAsync({
        id: existing?.id,
        customerId,
        customerName: customerName || null,
        channel,
        occurredAt: occurredAtFrom(occurredOn, existing?.occurred_at ?? null),
        interaction,
        repNotes: repNotes || null,
      });
      onOpenChange(false);
      onSaved?.(saved.id);
      if (extract) {
        toast.success(
          existing ? 'Entry updated — extracting tasks…' : 'Logged — extracting tasks…',
        );
        process.mutate(
          { feedbackId: saved.id },
          {
            onSuccess: (res) => {
              const n = res.tasks.length;
              toast.success(
                n === 0
                  ? 'Processed — nothing actionable found.'
                  : `Found ${n} task${n === 1 ? '' : 's'} to review.`,
              );
            },
            onError: (err) => toast.error(`Extraction failed — ${describeFeedbackError(err)}`),
          },
        );
      } else {
        toast.success(existing ? 'Entry updated.' : 'Interaction logged.');
      }
    } catch (err) {
      toast.error(describeFeedbackError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(true);
          }}
          className="min-w-0 space-y-5"
        >
          <DialogHeader>
            <DialogTitle>
              {existing ? 'Edit interaction' : 'Log a customer interaction'}
            </DialogTitle>
            <DialogDescription>
              Paste the email, call notes, chat or review as-is, then add your own take. The
              extraction pulls out the things someone needs to go and do; you decide which ones to
              send.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto_auto]">
            <div className="space-y-2" ref={customerFieldRef}>
              <Label htmlFor="feedback-customer">Customer</Label>
              <div className="relative">
                <Input
                  id="feedback-customer"
                  value={customerName}
                  onChange={(e) => {
                    setCustomerName(e.target.value);
                    setCustomerId(null);
                    setCustomerMenuOpen(true);
                  }}
                  onFocus={() => setCustomerMenuOpen(true)}
                  placeholder="Company or person"
                  autoComplete="off"
                  className={cn(linkedCustomer && 'pr-9')}
                />
                {linkedCustomer && (
                  <span
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-bright-moss-dark"
                    title="Linked to a catalog customer"
                  >
                    <Check className="h-4 w-4" />
                  </span>
                )}
                {customerMenuOpen && matches.length > 0 && !linkedCustomer && (
                  <ul
                    role="listbox"
                    className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-md"
                  >
                    {matches.map((c) => (
                      <li key={c.id} role="option" aria-selected={false}>
                        <button
                          type="button"
                          onClick={() => {
                            setCustomerId(c.id);
                            setCustomerName(c.name);
                            setCustomerMenuOpen(false);
                          }}
                          className="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary"
                        >
                          <span className="truncate">{c.name}</span>
                          {c.email && (
                            <span className="truncate text-xs text-muted-foreground">
                              {c.email}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {linkedCustomer
                  ? 'Linked to the catalog customer.'
                  : 'Pick a catalog customer or just type a name.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Channel</Label>
              <Select
                value={channel}
                onValueChange={(v) => setChannel(v as CustomerFeedbackChannel)}
              >
                <SelectTrigger className="h-10 w-full sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FEEDBACK_CHANNELS.map((c) => {
                    const Icon = FEEDBACK_CHANNEL_META[c].icon;
                    return (
                      <SelectItem key={c} value={c}>
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          {FEEDBACK_CHANNEL_META[c].label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>When</Label>
              <DatePicker
                value={occurredOn}
                onChange={setOccurredOn}
                aria-label="Interaction date"
                className="h-10 w-full sm:w-40"
                showShortcuts
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-interaction">Customer interaction</Label>
            <Textarea
              id="feedback-interaction"
              value={interaction}
              onChange={(e) => setInteraction(e.target.value)}
              placeholder="Paste the email thread, call notes, chat transcript or review here…"
              className="min-h-[200px] font-mono text-[13px] leading-relaxed"
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-notes">Your notes</Label>
            <Textarea
              id="feedback-notes"
              value={repNotes}
              onChange={(e) => setRepNotes(e.target.value)}
              placeholder="Context the rest of the team needs: what you promised, what they really want, how big the account is…"
              className="min-h-[88px]"
            />
          </div>

          <DialogFooter className="mt-2 gap-2 sm:justify-between">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!canSubmit}
                onClick={() => void submit(false)}
              >
                {existing ? 'Save' : 'Save only'}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {existing ? 'Save & re-extract' : 'Save & extract tasks'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
